import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { Edit, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PetFormHeader } from "../components/PetFormHeader";
import {
  getPetMedical,
  createMedicalRecord,
  updateMedicalRecord,
  deleteMedicalRecord,
  getPetBloodTest,
  createBloodTestRecord,
  updateBloodTestRecord,
  deleteBloodTestRecord,
} from "../services/api";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { validateDate, convertFromISO } from "../utils/dateValidation";
import DatePickerField from "../components/DatePickerField";
import { useTranslation } from 'react-i18next';
import { set } from "lodash";

const Label = ({ children, htmlFor, className, ...props }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    className={className}
    variant="body2"
    color="textPrimary"
    {...props}
    sx={{ fontWeight: 700, mb: 1, ...props.sx }}
  >
    {children}
  </Typography>
);

const treatmentOptions = ["徵狀治療", "藥物治療", "物理治療", "安排手術"];

const PetMedical = () => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const [isEditMode, setIsEditMode] = useState(false);
  const { petId } = useParams();
  const medicalFormRef = useRef(null);
  const bloodTestFormRef = useRef(null);
  const location = useLocation();
  const initialpetData = location.state?.petData;
  const [petData, setPetData] = useState(initialpetData);
  const [medicalInfo, setMedicalInfo] = useState({});
  const navigate = useNavigate();
  const [showMedicalForm, setShowMedicalForm] = useState(false);
  const [showBloodTestForm, setShowBloodTestForm] = useState(false);
  const [editingMedicalIndex, setEditingMedicalIndex] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null); // Store the actual record being edited
  const [dateErrors, setDateErrors] = useState({});

  // Blood Test specific states
  const [editingBloodTestIndex, setEditingBloodTestIndex] = useState(null);
  const [editingBloodTestRecord, setEditingBloodTestRecord] = useState(null);
  const [bloodTestDateErrors, setBloodTestDateErrors] = useState({});
  const [buttonLoadingBloodTest, setButtonLoadingBloodTest] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteBloodTestModal, setShowDeleteBloodTestModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteBloodTestLoading, setDeleteBloodTestLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [deletingMedicalId, setDeletingMedicalId] = useState("");
  const [deletingBloodTestId, setDeletingBloodTestId] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [BackColor, setBackColor] = useState("");
  const [UpdateMessage, setUpdateMessage] = useState("");
  const { t } = useTranslation();
  const [form, setForm] = useState({
    medicalDate: "",
    medicalPlace: "",
    medicalDoctor: "",
    medicalResult: "",
    medicalSolution: "",
  });
  const [bloodTestForm, setBloodTestForm] = useState({
    bloodTestDate: "",
    Heartworm: "",
    LymeDisease: "",
    Ehrlichiosis: "",
    Anaplasmosis: "",
    Babesiosis: "",
  });
  const treatmentOptions = [t("SymptomaticTreatment"), t("MedicinalTreatment"), t("PhysicalTherapy"), t("Surgery")];

  // Sorting state for Medical Records
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"
  const [sortedRecords, setSortedRecords] = useState([]);

  // Sorting state for Blood Test Records
  const [sortOrderBloodTest, setSortOrderBloodTest] = useState("desc");
  const [sortedBloodTestRecords, setSortedBloodTestRecords] = useState([]);

  // Sorting function for Medical Records
  const sortRecordsByDate = (records, order) => {
    return [...records].sort((a, b) => {
      const dateA = new Date(a.medicalDate.split("/").reverse().join("-")); // Convert DD/MM/YYYY to YYYY-MM-DD for proper sorting
      const dateB = new Date(b.medicalDate.split("/").reverse().join("-"));

      if (order === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  // Sorting function for Blood Test Records
  const sortBloodTestRecordsByDate = (records, order) => {
    return [...records].sort((a, b) => {
      const dateA = new Date(a.bloodTestDate.split("/").reverse().join("-"));
      const dateB = new Date(b.bloodTestDate.split("/").reverse().join("-"));

      if (order === "asc") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  // Handle sorting for Medical Records
  const handleSort = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    const sorted = sortRecordsByDate(medicalInfo.medical || [], newOrder);
    setSortedRecords(sorted);
  };

  // Handle sorting for Blood Test Records
  const handleBloodTestSort = () => {
    const newOrder = sortOrderBloodTest === "asc" ? "desc" : "asc";
    setSortOrderBloodTest(newOrder);
    const sorted = sortBloodTestRecordsByDate(medicalInfo.bloodTest || [], newOrder);
    setSortedBloodTestRecords(sorted);
  };

  // Fetch pet data if editing
  useEffect(() => {
    if (location.pathname.includes("edit") && petData) {
      setIsEditMode(true);
      const formattedData = {
        ...petData,
        medical: petData.medical
          ? petData.medical.map((record) => ({
            ...record,
            medicalDate: convertFromISO(record.medicalDate),
          }))
          : [],
        bloodTest: petData.bloodTest
          ? petData.bloodTest.map((record) => ({
            ...record,
            bloodTestDate: convertFromISO(record.bloodTestDate),
            Heartworm: record.heartworm || record.Heartworm || "",
            LymeDisease: record.lymeDisease || record.LymeDisease || "",
            Ehrlichiosis: record.ehrlichiosis || record.Ehrlichiosis || "",
            Anaplasmosis: record.anaplasmosis || record.Anaplasmosis || "",
            Babesiosis: record.babesiosis || record.Babesiosis || "",
          }))
          : [],
      };

      setMedicalInfo((prev) => ({
        ...prev,
        ...formattedData,
      }));

      // Set initial sorted medical records
      if (formattedData.medical && Array.isArray(formattedData.medical)) {
        const sorted = sortRecordsByDate(formattedData.medical, sortOrder);
        setSortedRecords(sorted);
      }

      // Set initial sorted blood test records
      if (formattedData.bloodTest && Array.isArray(formattedData.bloodTest)) {
        const sortedBlood = sortBloodTestRecordsByDate(formattedData.bloodTest, sortOrderBloodTest);
        setSortedBloodTestRecords(sortedBlood);
      }
    }
  }, [location, petData]);

  // Update sorted records when medicalInfo.medical or sortOrder changes
  useEffect(() => {
    if (medicalInfo.medical && Array.isArray(medicalInfo.medical)) {
      const sorted = sortRecordsByDate(medicalInfo.medical, sortOrder);
      setSortedRecords(sorted);
    }
  }, [medicalInfo.medical, sortOrder]);

  // Update sorted blood test records when medicalInfo.bloodTest or sortOrderBloodTest changes
  useEffect(() => {
    if (medicalInfo.bloodTest && Array.isArray(medicalInfo.bloodTest)) {
      const sorted = sortBloodTestRecordsByDate(medicalInfo.bloodTest, sortOrderBloodTest);
      setSortedBloodTestRecords(sorted);
    }
  }, [medicalInfo.bloodTest, sortOrderBloodTest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    setButtonLoading(true);
    e.preventDefault();
    try {
      // Validate date before adding
      if (form.medicalDate) {
        const dateValidation = validateDate(form.medicalDate);
        if (!dateValidation.isValid) {
          setButtonLoading(false);
          setDateErrors({
            ...dateErrors,
            medicalDate: dateValidation.error,
          });
          setError(true);
          setBackColor("#d32f2f");
          setUpdateMessage(t('ErrorSavingPet'));
          setTimeout(() => {
            setError(false);
          }, 2000);
          return;
        }
      }

      const medicalData = {
        medicalDate: form.medicalDate,
        medicalPlace: form.medicalPlace,
        medicalDoctor: form.medicalDoctor,
        medicalResult: form.medicalResult,
        medicalSolution: form.medicalSolution,
      };

      if (editingMedicalIndex === null) {
        // Add new medical record
        await createMedicalRecord(petId, medicalData);
      } else {
        // Edit existing medical record
        await updateMedicalRecord(
          petId,
          editingRecord._id,
          medicalData
        );

      }

      // Refresh the pet data to get updated medical list
      const petResponse = await getPetMedical(petId);
      const APImedicalData = petResponse.data.form;
      const formattedData = {
        ...APImedicalData,
        medical: APImedicalData.medical
          ? APImedicalData.medical.map((record) => ({
            ...record,
            medicalDate: convertFromISO(record.medicalDate),
          }))
          : [],
      };
      setMedicalInfo((prev) => ({
        ...prev,
        ...formattedData,
      }));
      setPetData((petData) => ({
        ...petData,
        medical: APImedicalData.medical,
      }));

      setShowMedicalForm(false);
      setEditingMedicalIndex(null);
      setEditingRecord(null);
      setDateErrors({});
      setForm({
        medicalDate: "",
        medicalPlace: "",
        medicalDoctor: "",
        medicalResult: "",
        medicalSolution: "",
      });
      setButtonLoading(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error) {
      setButtonLoading(false);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error saving medical record:", error);
    }
  };

  const handleMedicalDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteMedicalRecord(
        petId,
        deletingMedicalId
      );

      // Refresh the pet data to get updated medical list
      const petResponse = await getPetMedical(
        petId
      );
      const APImedicalData = petResponse.data.form;
      const formattedData = {
        ...APImedicalData,
        medical: APImedicalData.medical
          ? APImedicalData.medical.map((record) => ({
            ...record,
            medicalDate: convertFromISO(
              record.medicalDate
            ),
          }))
          : [],
      };
      setMedicalInfo((prev) => ({
        ...prev,
        ...formattedData,
      }));
      setPetData((petData) => ({
        ...petData,
        medical: APImedicalData.medical,
      }));
      setShowMedicalForm(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error) {
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error deleting medical record:", error);
    } finally {
      setShowDeleteModal(false);
      setDeleteLoading(false);
    }
  }

  // Handle Blood Test Submit
  const handleBloodTestSubmit = async (e) => {
    setButtonLoadingBloodTest(true);
    e.preventDefault();
    try {
      // Validate date before adding
      if (bloodTestForm.bloodTestDate) {
        const dateValidation = validateDate(bloodTestForm.bloodTestDate);
        if (!dateValidation.isValid) {
          setButtonLoadingBloodTest(false);
          setBloodTestDateErrors({
            ...bloodTestDateErrors,
            bloodTestDate: dateValidation.error,
          });
          setError(true);
          setBackColor("#d32f2f");
          setUpdateMessage(t('ErrorSavingPet'));
          setTimeout(() => {
            setError(false);
          }, 2000);
          return;
        }
      }

      const bloodTestData = {
        bloodTestDate: bloodTestForm.bloodTestDate,
        heartworm: bloodTestForm.Heartworm,
        lymeDisease: bloodTestForm.LymeDisease,
        ehrlichiosis: bloodTestForm.Ehrlichiosis,
        anaplasmosis: bloodTestForm.Anaplasmosis,
        babesiosis: bloodTestForm.Babesiosis,
      };

      if (editingBloodTestIndex === null) {
        // Add new blood test record
        await createBloodTestRecord(petId, bloodTestData);
      } else {
        // Edit existing blood test record
        await updateBloodTestRecord(petId, editingBloodTestRecord._id, bloodTestData);
      }

      // Refresh the pet data to get updated blood test list
      const petResponse = await getPetBloodTest(petId);
      const APIbloodTestData = petResponse.data.form;
      const formattedData = {
        bloodTest: APIbloodTestData.blood_test
          ? APIbloodTestData.blood_test.map((record) => ({
            ...record,
            bloodTestDate: convertFromISO(record.bloodTestDate),
            Heartworm: record.heartworm || record.Heartworm || "",
            LymeDisease: record.lymeDisease || record.LymeDisease || "",
            Ehrlichiosis: record.ehrlichiosis || record.Ehrlichiosis || "",
            Anaplasmosis: record.anaplasmosis || record.Anaplasmosis || "",
            Babesiosis: record.babesiosis || record.Babesiosis || "",
          }))
          : [],
      };
      setMedicalInfo((prev) => ({
        ...prev,
        ...formattedData,
      }));
      setPetData((petData) => ({
        ...petData,
        bloodTest: APIbloodTestData.blood_test,
      }));

      setShowBloodTestForm(false);
      setEditingBloodTestIndex(null);
      setEditingBloodTestRecord(null);
      setBloodTestDateErrors({});
      setBloodTestForm({
        bloodTestDate: "",
        Heartworm: "",
        LymeDisease: "",
        Ehrlichiosis: "",
        Anaplasmosis: "",
        Babesiosis: "",
      });
      setButtonLoadingBloodTest(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error) {
      setButtonLoadingBloodTest(false);
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error saving blood test record:", error);
    }
  };

  // Handle Blood Test Delete
  const handleBloodTestDelete = async () => {
    setDeleteBloodTestLoading(true);
    try {
      await deleteBloodTestRecord(petId, deletingBloodTestId);

      // Refresh the pet data to get updated blood test list
      const petResponse = await getPetBloodTest(petId);
      const APIbloodTestData = petResponse.data.form;
      const formattedData = {
        bloodTest: APIbloodTestData.blood_test
          ? APIbloodTestData.blood_test.map((record) => ({
            ...record,
            bloodTestDate: convertFromISO(record.bloodTestDate),
            Heartworm: record.heartworm || "",
            LymeDisease: record.lymeDisease || "",
            Ehrlichiosis: record.ehrlichiosis || "",
            Anaplasmosis: record.anaplasmosis || "",
            Babesiosis: record.babesiosis || "",
          }))
          : [],
      };
      setMedicalInfo((prev) => ({
        ...prev,
        ...formattedData,
      }));
      setPetData((petData) => ({
        ...petData,
        bloodTest: APIbloodTestData.blood_test,
      }));
      setShowBloodTestForm(false);
      setSuccess(true);
      setBackColor("#4CAF50");
      setUpdateMessage(t('PetUpdated'));
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error) {
      setError(true);
      setBackColor("#d32f2f");
      setUpdateMessage(t('ErrorSavingPet'));
      setTimeout(() => {
        setError(false);
      }, 2000);
      console.error("Error deleting blood test record:", error);
    } finally {
      setShowDeleteBloodTestModal(false);
      setDeleteBloodTestLoading(false);
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: "#FCF8F3",
        minHeight: "100vh",
        pt: isMobile ? 12 : 16,
      }}
    >
      <Navbar />
      <Box
        sx={{
          maxWidth: 1440,
          mx: "auto",
          mb: { xs: 5, md: 10 },
          px: { xs: 2, sm: 3, md: 0 },
        }}
      >
        {/* Tabs */}
        <PetFormHeader
          currentPageId="medical"
          isEditMode={isEditMode}
          petId={isEditMode ? petId : null}
          handlePageNavigation={() => { }}
          petData={petData}
        />
        <Box component="form" onSubmit={handleSubmit}>
          <Typography fontWeight={700} sx={{ fontSize: 18, mb: 4, px: 3 }}>
            {t("MedicalRecords")}
            <Button
              sx={{
                ml: 2,
                fontWeight: 700,
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
              variant="outlined"
              color="text.secondary"
              onClick={() => {
                setShowMedicalForm(true);
                setEditingMedicalIndex(null);
                setEditingRecord(null);
                setForm({
                  medicalDate: "",
                  medicalPlace: "",
                  medicalDoctor: "",
                  medicalResult: "",
                  medicalSolution: "",
                });
              }}
              disabled={showMedicalForm}
            >
              + {t("AddNewRecord")}
            </Button>
          </Typography>

          {medicalInfo.medical && medicalInfo.medical.length > 0 && (
            <Box sx={{ px: 3, mb: 4 }}>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: 1,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { backgroundColor: "#e8e8e8" },
                          }}
                          onClick={handleSort}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {t("AppointmentDate")}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                ml: 0.5,
                              }}
                            >
                              {sortOrder === "asc" ? (
                                <ArrowUp size={16} />
                              ) : (
                                <ArrowDown size={16} />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t("Clinic/HospitalLocation")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("DoctorName")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("DiagnosisResult")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("TreatmentPlan")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRecords.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>{record.medicalDate}</TableCell>
                          <TableCell>{record.medicalPlace}</TableCell>
                          <TableCell>{record.medicalDoctor}</TableCell>
                          <TableCell>{record.medicalResult}</TableCell>
                          <TableCell>{record.medicalSolution}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => {
                                setShowMedicalForm(true);
                                setEditingMedicalIndex(index);
                                setEditingRecord(record);
                                setForm({
                                  ...record,
                                  medicalDate: record.medicalDate,
                                });
                                if (isMobile) {
                                  setTimeout(() => {
                                    medicalFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }, 100);
                                }
                              }}
                              size="small"
                              sx={{ mr: 1 }}
                            >
                              <Edit size={16} />
                            </IconButton>
                            <IconButton
                              onClick={async () => {
                                const recordToDelete = sortedRecords[index];
                                setDeletingMedicalId(recordToDelete._id);
                                setShowDeleteModal(true);
                              }}
                              size="small"
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Simple Text View */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", mb: 2, px: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#666" }}
                  >
                    {t("sortby")}
                  </Typography>
                  <Button
                    onClick={handleSort}
                    size="small"
                    sx={{
                      ml: 1,
                      color: "#FFB60C",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "rgba(255,182,12,0.08)" },
                    }}
                  >
                    {t("AppointmentDate")} {sortOrder === "asc" ? "▲" : "▼"}
                  </Button>
                </Box>
                {sortedRecords.map((record, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 4,
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: 16, color: "#333" }}
                      >
                        {t("MedicalRecords")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => {
                            setShowMedicalForm(true);
                            setEditingMedicalIndex(index);
                            setEditingRecord(record);
                            setForm({
                              ...record,
                              medicalDate: record.medicalDate,
                            });
                            setTimeout(() => {
                              medicalFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                          }}
                          size="small"
                          sx={{ color: "#FFB60C" }}
                        >
                          <Edit size={20} />
                        </IconButton>
                        <IconButton
                          onClick={async () => {
                            const recordToDelete = sortedRecords[index];
                            setDeletingMedicalId(recordToDelete._id);
                            setShowDeleteModal(true);
                          }}
                          size="small"
                          sx={{ color: "#A0A0A0" }}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* {t("AppointmentDate")} */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("AppointmentDate")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.medicalDate || "-"}
                        </Typography>
                      </Box>

                      {/* 診所 / 醫院 */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          診所 / 醫院
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.medicalPlace || "-"}
                        </Typography>
                      </Box>

                      {/* {t("DoctorName")} */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("DoctorName")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.medicalDoctor || "-"}
                        </Typography>
                      </Box>

                      {/* {t("DiagnosisResult")} */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("DiagnosisResult")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.medicalResult || "-"}
                        </Typography>
                      </Box>

                      {/* {t("TreatmentPlan")} */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("TreatmentPlan")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.medicalSolution || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {showMedicalForm && (
            <>
              <Grid
                ref={medicalFormRef}
                container
                spacing={3}
                justifyContent="space-between"
                sx={{ px: 3, backgroundColor: "#FFF2E2", py: 3,}}
              >
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="medicalDate">{t("AppointmentDate")}</Label>
                  <DatePickerField
                    id="medicalDate"
                    name="medicalDate"
                    placeholder="DD/MM/YYYY"
                    value={form.medicalDate}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, medicalDate: value }))
                    }
                    onError={(error) =>
                      setDateErrors({ ...dateErrors, medicalDate: error })
                    }
                    error={!!dateErrors.medicalDate}
                    helperText={dateErrors.medicalDate}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="medicalPlace">{t("Clinic/HospitalLocation")}</Label>
                  <TextField
                    id="medicalPlace"
                    name="medicalPlace"
                    placeholder={t("Clinic/HospitalLocation")}
                    value={form.medicalPlace}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="medicalDoctor">{t("DoctorName")}</Label>
                  <TextField
                    id="medicalDoctor"
                    name="medicalDoctor"
                    placeholder={t("DoctorName")}
                    value={form.medicalDoctor}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={3} sx={{ px: 3, backgroundColor: "#FFF2E2", }}>
                <Grid
                  item
                  xs={12}
                  md={12}
                  sx={{ maxWidth: 350, width: "100%" }}
                >
                  <Label htmlFor="medicalResult">{t("DiagnosisResult")}</Label>
                  <TextField
                    id="medicalResult"
                    name="medicalResult"
                    placeholder={t("DiagnosisResult")}
                    value={form.medicalResult}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ px: 3, backgroundColor: "#FFF2E2", py: 3 }}>
                <Label>{t("TreatmentPlan")}</Label>
                <RadioGroup
                  row={isMobile ? false : true}
                  value={form.medicalSolution}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      medicalSolution: e.target.value,
                    }));
                  }}
                  sx={{ gap: isMobile ? 1 : 4 }}
                >
                  {treatmentOptions.map((option) => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={<Radio />}
                      label={option}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                  ))}
                  <FormControlLabel
                    value={t("Other")}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {t("Other")}：
                        <TextField
                          value={
                            form.medicalSolution &&
                              !treatmentOptions.includes(form.medicalSolution)
                              ? form.medicalSolution
                              : ""
                          }
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              medicalSolution: e.target.value,
                            }))
                          }
                          placeholder=""
                          size="small"
                          sx={{ ml: 1, width: 100, backgroundColor: "white" }}
                        />
                      </Box>
                    }
                    sx={{ mr: 0, mb: isMobile ? 1 : 0 }}
                  />
                </RadioGroup>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 2,
                    py: 3,
                    pb: 3
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={buttonLoading}
                    sx={{
                      backgroundColor: "#FFB60C",
                      color: "#050505",
                      minWidth: 120,
                      "&:hover": { backgroundColor: "#FFB60C" },
                      "&:disabled": { background: "rgba(255, 182, 12, 0.5)" },
                      cursor: buttonLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                    onClick={handleSubmit}
                  >
                    {t('Save')}
                    {buttonLoading && (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <CircularProgress size={15} color="inherit" />
                      </span>
                    )}
                  </Button>
                  <Button
                    variant="text"
                    color="inherit"
                    sx={{ color: "#A0A0A0", minWidth: 120, fontWeight: 700 }}
                    onClick={() => {
                      setShowMedicalForm(false);
                      setEditingMedicalIndex(null);
                      setEditingRecord(null);
                      setForm({
                        medicalDate: "",
                        medicalPlace: "",
                        medicalDoctor: "",
                        medicalResult: "",
                        medicalSolution: "",
                      });
                    }}
                  >
                    {t("cancel")}
                  </Button>
                </Box>
              </Box>
            </>
          )}

          <Typography fontWeight={700} sx={{ fontSize: 18, my: 4, px: 3 }}>
            {t("BloodTestRecords")}
            <Button
              sx={{
                ml: 2,
                fontWeight: 700,
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
              variant="outlined"
              color="text.secondary"
              onClick={() => {
                setShowBloodTestForm(true);
                setEditingBloodTestIndex(null);
                setEditingBloodTestRecord(null);
                setBloodTestForm({
                  bloodTestDate: "",
                  Heartworm: "",
                  LymeDisease: "",
                  Ehrlichiosis: "",
                  Anaplasmosis: "",
                  Babesiosis: "",
                });
              }}
              disabled={showBloodTestForm}
            >
              + {t("AddNewRecord")}
            </Button>
          </Typography>

          {medicalInfo.bloodTest && medicalInfo.bloodTest.length > 0 && (
            <Box sx={{ px: 3, mb: 4 }}>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: 1,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover": { backgroundColor: "#e8e8e8" },
                          }}
                          onClick={handleBloodTestSort}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {t("BloodRoutineDate")}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                ml: 0.5,
                              }}
                            >
                              {sortOrderBloodTest === "asc" ? (
                                <ArrowUp size={16} />
                              ) : (
                                <ArrowDown size={16} />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Heartworm")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("LymeDisease")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Ehrlichiosis")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Anaplasmosis")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Babesiosis")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedBloodTestRecords.map((record, index) => (
                        <TableRow key={index}>
                          <TableCell>{record.bloodTestDate}</TableCell>
                          <TableCell>{record.Heartworm || "-"}</TableCell>
                          <TableCell>{record.LymeDisease || "-"}</TableCell>
                          <TableCell>{record.Ehrlichiosis || "-"}</TableCell>
                          <TableCell>{record.Anaplasmosis || "-"}</TableCell>
                          <TableCell>{record.Babesiosis || "-"}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => {
                                setShowBloodTestForm(true);
                                setEditingBloodTestIndex(index);
                                setEditingBloodTestRecord(record);
                                setBloodTestForm({
                                  ...record,
                                  bloodTestDate: record.bloodTestDate,
                                });
                                if (isMobile) {
                                  setTimeout(() => {
                                    bloodTestFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }, 100);
                                }
                              }}
                              size="small"
                              sx={{ mr: 1 }}
                            >
                              <Edit size={16} />
                            </IconButton>
                            <IconButton
                              onClick={async () => {
                                const recordToDelete = sortedBloodTestRecords[index];
                                setDeletingBloodTestId(recordToDelete._id);
                                setShowDeleteBloodTestModal(true);
                              }}
                              size="small"
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Simple Text View */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", mb: 2, px: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#666" }}
                  >
                    {t("sortby")}
                  </Typography>
                  <Button
                    onClick={handleBloodTestSort}
                    size="small"
                    sx={{
                      ml: 1,
                      color: "#FFB60C",
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "rgba(255,182,12,0.08)" },
                    }}
                  >
                    {t("BloodRoutineDate")} {sortOrderBloodTest === "asc" ? "▲" : "▼"}
                  </Button>
                </Box>
                {sortedBloodTestRecords.map((record, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 4,
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, fontSize: 16, color: "#333" }}
                      >
                        {t("BloodTestRecords")}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          onClick={() => {
                            setShowBloodTestForm(true);
                            setEditingBloodTestIndex(index);
                            setEditingBloodTestRecord(record);
                            setBloodTestForm({
                              ...record,
                              bloodTestDate: record.bloodTestDate,
                            });
                            setTimeout(() => {
                              bloodTestFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                          }}
                          size="small"
                          sx={{ color: "#FFB60C" }}
                        >
                          <Edit size={20} />
                        </IconButton>
                        <IconButton
                          onClick={async () => {
                            const recordToDelete = sortedBloodTestRecords[index];
                            setDeletingBloodTestId(recordToDelete._id);
                            setShowDeleteBloodTestModal(true);
                          }}
                          size="small"
                          sx={{ color: "#A0A0A0" }}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("BloodRoutineDate")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.bloodTestDate || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("Heartworm")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.Heartworm || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("LymeDisease")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.LymeDisease || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("Ehrlichiosis")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.Ehrlichiosis || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("Anaplasmosis")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.Anaplasmosis || "-"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: "#666", mb: 0.5 }}
                        >
                          {t("Babesiosis")}
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#333" }}>
                          {record.Babesiosis || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {showBloodTestForm && (
            <>
              <Grid
                ref={bloodTestFormRef}
                container
                spacing={3}
                justifyContent="space-between"
                sx={{ px: 3, backgroundColor: "#FFF2E2", py: 3, }}
              >
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label htmlFor="bloodTestDate">{t("BloodRoutineDate")}</Label>
                  <DatePickerField
                    id="bloodTestDate"
                    name="bloodTestDate"
                    placeholder="DD/MM/YYYY"
                    value={bloodTestForm.bloodTestDate}
                    onChange={(value) =>
                      setBloodTestForm((prev) => ({ ...prev, bloodTestDate: value }))
                    }
                    onError={(error) =>
                      setBloodTestDateErrors({ ...bloodTestDateErrors, bloodTestDate: error })
                    }
                    error={!!bloodTestDateErrors.bloodTestDate}
                    helperText={bloodTestDateErrors.bloodTestDate}
                  />
                </Grid>
              </Grid>

              <Typography sx={{ px: 3, backgroundColor: "#FFF2E2", py: 1, fontSize: 20 }}>
                4DX
              </Typography>

              <Grid
                container
                spacing={3}
                justifyContent="space-between"
                sx={{ backgroundColor: "#FFF2E2", px: 3, py: 3 }}
              >
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%", }}>
                  <Label>{t("Heartworm")}</Label>
                  <RadioGroup
                    row
                    value={bloodTestForm.Heartworm}
                    onChange={(e) => {
                      setBloodTestForm((prev) => ({
                        ...prev,
                        Heartworm: e.target.value,
                      }));
                    }}
                    sx={{ gap: 4 }}
                  >
                    <FormControlLabel
                      value="Positive"
                      control={<Radio />}
                      label={t("Positive")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Negative"
                      control={<Radio />}
                      label={t("Negative")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Unknown"
                      control={<Radio />}
                      label={t("unknown")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label>{t("LymeDisease")}</Label>
                  <RadioGroup
                    row
                    value={bloodTestForm.LymeDisease}
                    onChange={(e) => {
                      setBloodTestForm((prev) => ({
                        ...prev,
                        LymeDisease: e.target.value,
                      }));
                    }}
                    sx={{ gap: 4 }}
                  >
                    <FormControlLabel
                      value="Positive"
                      control={<Radio />}
                      label={t("Positive")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Negative"
                      control={<Radio />}
                      label={t("Negative")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Unknown"
                      control={<Radio />}
                      label={t("unknown")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label>{t("Ehrlichiosis")}</Label>
                  <RadioGroup
                    row
                    value={bloodTestForm.Ehrlichiosis}
                    onChange={(e) => {
                      setBloodTestForm((prev) => ({
                        ...prev,
                        Ehrlichiosis: e.target.value,
                      }));
                    }}
                    sx={{ gap: 4 }}
                  >
                    <FormControlLabel
                      value="Positive"
                      control={<Radio />}
                      label={t("Positive")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Negative"
                      control={<Radio />}
                      label={t("Negative")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Unknown"
                      control={<Radio />}
                      label={t("unknown")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label>{t("Anaplasmosis")}</Label>
                  <RadioGroup
                    row
                    value={bloodTestForm.Anaplasmosis}
                    onChange={(e) => {
                      setBloodTestForm((prev) => ({
                        ...prev,
                        Anaplasmosis: e.target.value,
                      }));
                    }}
                    sx={{ gap: 4 }}
                  >
                    <FormControlLabel
                      value="Positive"
                      control={<Radio />}
                      label={t("Positive")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Negative"
                      control={<Radio />}
                      label={t("Negative")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Unknown"
                      control={<Radio />}
                      label={t("unknown")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                  <Label>{t("Babesiosis")}</Label>
                  <RadioGroup
                    row
                    value={bloodTestForm.Babesiosis}
                    onChange={(e) => {
                      setBloodTestForm((prev) => ({
                        ...prev,
                        Babesiosis: e.target.value,
                      }));
                    }}
                    sx={{ gap: 4 }}
                  >
                    <FormControlLabel
                      value="Positive"
                      control={<Radio />}
                      label={t("Positive")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Negative"
                      control={<Radio />}
                      label={t("Negative")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                    <FormControlLabel
                      value="Unknown"
                      control={<Radio />}
                      label={t("unknown")}
                      sx={{ mr: isMobile ? 0 : 4, mb: isMobile ? 1 : 0 }}
                    />
                  </RadioGroup>
                </Grid>
                <Grid item xs={12} md={4} sx={{ maxWidth: 350, width: "100%" }}>
                </Grid>
              </Grid>

              {/* Blood Test Form Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  gap: 2,
                  px: 3,
                  py: 3,
                  pb: 3,
                  backgroundColor: "#FFF2E2",
                  mb: 5,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={buttonLoadingBloodTest}
                  sx={{
                    backgroundColor: "#FFB60C",
                    color: "#050505",
                    minWidth: 120,
                    "&:hover": { backgroundColor: "#FFB60C" },
                    "&:disabled": { background: "rgba(255, 182, 12, 0.5)" },
                    cursor: buttonLoadingBloodTest ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                  onClick={handleBloodTestSubmit}
                >
                  {t('Save')}
                  {buttonLoadingBloodTest && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <CircularProgress size={15} color="inherit" />
                    </span>
                  )}
                </Button>
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ color: "#A0A0A0", minWidth: 120, fontWeight: 700 }}
                  onClick={() => {
                    setShowBloodTestForm(false);
                    setEditingBloodTestIndex(null);
                    setEditingBloodTestRecord(null);
                    setBloodTestForm({
                      bloodTestDate: "",
                      Heartworm: "",
                      LymeDisease: "",
                      Ehrlichiosis: "",
                      Anaplasmosis: "",
                      Babesiosis: "",
                    });
                  }}
                >
                  {t("cancel")}
                </Button>
              </Box>
            </>
          )}


          {/* Bottom Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              mt: { xs: 5, md: 10 },
              mr: 3,
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              color="text.secondary"
              onClick={() => navigate("/pet-management")}
              sx={{
                minWidth: 120,
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
            >
              {t("BackToHome")}
            </Button>
          </Box>
        </Box>
      </Box>
      <Footer />
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: (success || error) ? 20 : -400,
          backgroundColor: BackColor,
          color: "#fff",
          padding: "16px 24px",
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 9999,
          minWidth: 250,
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: (success || error)
            ? "right 0.4s ease-out"
            : "right 0.4s ease-in",
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {error ? (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke={BackColor}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </Box>
        <Typography fontWeight={600} fontSize={isMobile ? 14 : 16}>
          {UpdateMessage}
        </Typography>
      </Box>
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "center",
            gap: 1,
          }}
        >

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2947 3.86118L1.82466 18.0012C1.65003 18.3036 1.55763 18.6465 1.55665 18.9957C1.55567 19.3449 1.64615 19.6883 1.81909 19.9917C1.99202 20.2951 2.24139 20.5479 2.54237 20.725C2.84335 20.9021 3.18546 20.9973 3.53466 21.0012H20.4747C20.8239 20.9973 21.166 20.9021 21.467 20.725C21.7679 20.5479 22.0173 20.2951 22.1902 19.9917C22.3632 19.6883 22.4536 19.3449 22.4527 18.9957C22.4517 18.6465 22.3593 18.3036 22.1847 18.0012L13.7147 3.86118C13.5364 3.56729 13.2854 3.3243 12.9859 3.15567C12.6863 2.98703 12.3484 2.89844 12.0047 2.89844C11.6609 2.89844 11.323 2.98703 11.0235 3.15567C10.7239 3.3243 10.4729 3.56729 10.2947 3.86118Z" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 9V13" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 17H12.008" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          {t('ConfirmDeleteMedical')}
        </Box>
        <DialogContent sx={{ pt: 0, pb: 0 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: 16 }}
          >
            {t('Systemwilldelete')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#FF1A1A",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: 'none',
              "&:hover": { backgroundColor: "#d63e3eff" },
              cursor: deleteLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
            onClick={handleMedicalDelete}
          >
            {t('Delete')}
            {deleteLoading && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <CircularProgress size={15} color="inherit" />
              </span>
            )}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: 'none',
            }}
            onClick={() => setShowDeleteModal(false)}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blood Test Delete Modal */}
      <Dialog
        open={showDeleteBloodTestModal}
        onClose={() => setShowDeleteBloodTestModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            justifyContent: "center",
            gap: 1,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2947 3.86118L1.82466 18.0012C1.65003 18.3036 1.55763 18.6465 1.55665 18.9957C1.55567 19.3449 1.64615 19.6883 1.81909 19.9917C1.99202 20.2951 2.24139 20.5479 2.54237 20.725C2.84335 20.9021 3.18546 20.9973 3.53466 21.0012H20.4747C20.8239 20.9973 21.166 20.9021 21.467 20.725C21.7679 20.5479 22.0173 20.2951 22.1902 19.9917C22.3632 19.6883 22.4536 19.3449 22.4527 18.9957C22.4517 18.6465 22.3593 18.3036 22.1847 18.0012L13.7147 3.86118C13.5364 3.56729 13.2854 3.3243 12.9859 3.15567C12.6863 2.98703 12.3484 2.89844 12.0047 2.89844C11.6609 2.89844 11.323 2.98703 11.0235 3.15567C10.7239 3.3243 10.4729 3.56729 10.2947 3.86118Z" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 9V13" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 17H12.008" stroke="#FF1A1A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {t('ConfirmDeleteBloodTest')}
        </Box>
        <DialogContent sx={{ pt: 0, pb: 0 }}>
          <Typography
            variant="body1"
            sx={{ mb: 3, textAlign: "center", fontSize: 16 }}
          >
            {t('Systemwilldelete')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#FF1A1A",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: 'none',
              "&:hover": { backgroundColor: "#d63e3eff" },
              cursor: deleteBloodTestLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
            onClick={handleBloodTestDelete}
          >
            {t('Delete')}
            {deleteBloodTestLoading && (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <CircularProgress size={15} color="inherit" />
              </span>
            )}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: 'none',
            }}
            onClick={() => setShowDeleteBloodTestModal(false)}
          >
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PetMedical;
